import { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
  LabelList,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import { supabase } from '../lib/supabase';
import Kpi from '../components/Kpi';
import { formatDate, formatEgp, formatNumber } from '../lib/format';
import type { DonationsByCampaign, DonationsByDay } from '../lib/types';
import PageHeader from '../components/PageHeader';

// Campaign titles that follow the "الفئة — التخصص" pattern group under
// the prefix. Titles without a dash used to collapse into a single "أخرى"
// bucket, which lumped every unrelated standalone campaign together —
// operators saw one card labelled "أخرى" with a dozen unrelated
// donations behind it. Now a title without a dash becomes its own
// group (its full name), so each destination shows on its own card.
function splitCampaign(title: string): { group: string; sub: string } {
  const parts = title.split(' — ');
  if (parts.length === 1) return { group: parts[0].trim(), sub: parts[0].trim() };
  const [group, ...rest] = parts;
  return { group: group.trim(), sub: rest.join(' — ').trim() };
}

type CategorySummary = {
  name: string;
  paid: number;
  count: number;
};

const GROUP_COLORS = ['#009091', '#fec830', '#3b8f68', '#c8863a', '#5b6b78', '#a0453b', '#8757a3'];

export default function DonationsPage() {
  const [byCampaign, setByCampaign] = useState<DonationsByCampaign[]>([]);
  const [byDay, setByDay] = useState<DonationsByDay[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, d] = await Promise.all([
        supabase.from('donations_by_campaign').select('*'),
        supabase.from('donations_by_day').select('*').order('day', { ascending: false }).limit(30),
      ]);
      if (c.error) throw c.error;
      if (d.error) throw d.error;
      setByCampaign((c.data ?? []) as DonationsByCampaign[]);
      setByDay(((d.data ?? []) as DonationsByDay[]).slice().reverse());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'حصل خطأ');
    } finally {
      setLoading(false);
    }
  };

  const paidTotal = byCampaign.reduce((s, r) => s + Number(r.paid_total_egp), 0);
  // Current-calendar-month paid total, computed in Cairo time. The
  // `donations_by_day` view already buckets rows by Africa/Cairo date,
  // and `r.day` arrives as a plain `YYYY-MM-DD` string, so slicing the
  // first 7 chars gives its Cairo year-month. We derive today's Cairo
  // year-month via `Intl.DateTimeFormat` with `timeZone: 'Africa/Cairo'`
  // — Intl reads Egypt's tzdata, so DST (شتوي/صيفي) is handled
  // automatically without a hard-coded UTC offset. Donations always
  // land on the day their `created_at` implies in Cairo, regardless of
  // whether the donor's phone is inside or outside Egypt (the server's
  // stored UTC timestamp is the source of truth, and this KPI reads
  // it back through the Cairo-bucketed view).
  const currentMonthPaid = (() => {
    const cairoParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Africa/Cairo',
      year: 'numeric',
      month: '2-digit',
    }).formatToParts(new Date());
    const y = cairoParts.find((p) => p.type === 'year')?.value ?? '';
    const m = cairoParts.find((p) => p.type === 'month')?.value ?? '';
    const currentYearMonth = `${y}-${m}`;
    return byDay
      .filter((r) => String(r.day).slice(0, 7) === currentYearMonth)
      .reduce((s, r) => s + Number(r.paid_total_egp), 0);
  })();

  // Group by parent category, drop empty groups from the primary view.
  const groups = useMemo<CategorySummary[]>(() => {
    const map = new Map<string, CategorySummary>();
    for (const r of byCampaign) {
      const { group } = splitCampaign(r.title_ar);
      const entry = map.get(group) ?? { name: group, paid: 0, count: 0 };
      entry.paid += Number(r.paid_total_egp);
      entry.count += Number(r.paid_count);
      map.set(group, entry);
    }
    return Array.from(map.values()).sort((a, b) => b.paid - a.paid);
  }, [byCampaign]);

  const activeGroups = groups.filter((g) => g.paid > 0 || g.count > 0);

  const groupColor = (name: string) => {
    const i = groups.findIndex((g) => g.name === name);
    return GROUP_COLORS[(i < 0 ? 0 : i) % GROUP_COLORS.length];
  };

  // Show every specialty on the chart — operator wants to see all
  // targets side-by-side, including the empty ones (they read as
  // "لسه فاضية" at zero height instead of vanishing from the report).
  const chartByCampaign = useMemo(
    () =>
      byCampaign
        .map((r) => {
          const { group, sub } = splitCampaign(r.title_ar);
          return {
            name: sub,
            group,
            paid: Number(r.paid_total_egp),
            count: Number(r.paid_count),
          };
        })
        .sort((a, b) => b.paid - a.paid),
    [byCampaign],
  );

  const chartByDay = byDay.map((d) => ({
    day: formatDate(d.day),
    egp: Number(d.paid_total_egp),
  }));

  const overviewChartHeight = Math.max(240, chartByCampaign.length * 44 + 40);
  const now = new Date().toLocaleDateString('ar-EG', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'Africa/Cairo',
  });

  return (
    <>
      <PageHeader
        eyebrow="التبرعات"
        title="تقارير الحملات"
        subtitle="إجماليات وتحصيلات حسب الفئة، التخصص، واليوم"
        actions={
          !loading && byCampaign.length > 0 ? (
            <button className="report-print-btn" onClick={() => window.print()}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />
              </svg>
              حفظ PDF
            </button>
          ) : null
        }
      />

      <div className="print-only" style={{ marginBottom: 16, fontSize: 12, color: '#666' }}>
        <strong>تاريخ التقرير:</strong> {now}
      </div>

      <style>{`
        .cat-card {
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          background: #fff;
          border-inline-start-width: 4px;
          border-inline-start-style: solid;
          transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;
          cursor: default;
        }
        .cat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }
        .cat-card--active {
          background: rgba(0, 144, 145, 0.05);
        }
        .cat-share-bar {
          height: 8px;
          width: 100%;
          background: #eef1f4;
          border-radius: 999px;
          overflow: hidden;
        }
        .cat-share-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 400ms ease;
        }
        .rpt-legend {
          display: flex; flex-wrap: wrap; gap: 14px;
          margin-bottom: 14px;
          font-size: 12.5px; color: var(--text-secondary);
        }
        .rpt-legend .item {
          display: flex; align-items: center; gap: 8px;
          padding: 4px 10px; border-radius: 999px;
          background: #f7f8fa;
          cursor: default;
          transition: background 150ms ease;
        }
        .rpt-legend .item:hover { background: #eef1f4; }
        .rpt-legend .swatch {
          width: 12px; height: 12px; border-radius: 3px;
        }
        .campaign-row-bar {
          display: inline-block;
          height: 6px;
          border-radius: 999px;
          background: currentColor;
          vertical-align: middle;
        }
      `}</style>

      {error && <div className="error-inline">{error}</div>}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : (
        <>
          {/* ============ KPI row ============ */}
          <div className="kpi-grid">
            <Kpi label="إجمالي المحصل" value={formatEgp(paidTotal)} />
            <Kpi label="إجمالي تحصيل الشهر الحالي" value={formatEgp(currentMonthPaid)} />
          </div>

          {/* Status cards moved to سجل التبرعات (DonationsTransactionsPage)
              on 2026-09-09 — the operator wanted them right above the
              filterable rows they act on. */}

          {/* ============ Category breakdown ============ */}
          {activeGroups.length > 0 && (
            <div className="card">
              <h3>المحصل حسب الفئة</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, marginBottom: 16 }}>
                توزيع الإجمالي على كل فئة. حرّك المؤشر فوق أي كارت أو شريحة لتحديد الفئة.
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: activeGroups.length >= 2 ? 'minmax(260px, 320px) 1fr' : '1fr',
                  gap: 20,
                  alignItems: 'center',
                }}
              >
                {activeGroups.length >= 2 && (
                  <div dir="ltr" style={{ width: '100%', height: 240 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={activeGroups}
                          dataKey="paid"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={95}
                          paddingAngle={2}
                          onMouseEnter={(_, i) => setHoveredGroup(activeGroups[i]?.name ?? null)}
                          onMouseLeave={() => setHoveredGroup(null)}
                        >
                          {activeGroups.map((g) => (
                            <Cell
                              key={g.name}
                              fill={groupColor(g.name)}
                              fillOpacity={hoveredGroup && hoveredGroup !== g.name ? 0.28 : 1}
                              style={{ transition: 'fill-opacity 200ms ease', cursor: 'pointer' }}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number, _n, entry) => [
                            `${formatEgp(value)} (${paidTotal > 0 ? ((value / paidTotal) * 100).toFixed(1) : 0}٪)`,
                            (entry?.payload as { name?: string })?.name ?? '',
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 12,
                  }}
                >
                  {activeGroups.map((g) => {
                    const share = paidTotal > 0 ? (g.paid / paidTotal) * 100 : 0;
                    const color = groupColor(g.name);
                    const active = hoveredGroup === g.name;
                    return (
                      <div
                        key={g.name}
                        className={`cat-card ${active ? 'cat-card--active' : ''}`}
                        style={{ borderInlineStartColor: color }}
                        onMouseEnter={() => setHoveredGroup(g.name)}
                        onMouseLeave={() => setHoveredGroup(null)}
                      >
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{g.name}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, margin: '4px 0', color }}>
                          {formatEgp(g.paid)}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                          {formatNumber(g.count)} تبرع · {share.toFixed(1)}٪
                        </div>
                        <div className="cat-share-bar">
                          <div
                            className="cat-share-fill"
                            style={{
                              width: `${Math.max(share, 3)}%`,
                              background: color,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {/* ============ Sub-category detail ============ */}
          <div className="card">
            <h3>المحصل حسب التخصص</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, marginBottom: 16 }}>
              كل تخصص برقمه المستقل، مرتّب من الأعلى للأدنى. الألوان تعكس الفئة الأم.
            </p>

            {chartByCampaign.length === 0 ? (
              <div className="empty">مفيش تبرعات لسه.</div>
            ) : (
              <>
                <div className="rpt-legend">
                  {activeGroups.map((g) => (
                    <div
                      key={g.name}
                      className="item"
                      onMouseEnter={() => setHoveredGroup(g.name)}
                      onMouseLeave={() => setHoveredGroup(null)}
                    >
                      <span className="swatch" style={{ background: groupColor(g.name) }} />
                      {g.name}
                    </div>
                  ))}
                </div>

                <div dir="ltr" style={{ width: '100%', height: Math.max(320, overviewChartHeight * 0.7) }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={chartByCampaign}
                      margin={{ top: 28, right: 16, bottom: 88, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8ea" vertical={false} />
                      <XAxis
                        type="category"
                        dataKey="name"
                        interval={0}
                        tick={{ fontSize: 12, fill: '#253238' }}
                        angle={-32}
                        textAnchor="end"
                        height={88}
                      />
                      <YAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v: number) => formatEgp(v).replace('ج.م', '')}
                        orientation="right"
                      />
                      <Tooltip
                        formatter={(value: number) => formatEgp(value)}
                        labelFormatter={(label: string, payload) => {
                          const g = payload?.[0]?.payload?.group;
                          return g ? `${g} — ${label}` : label;
                        }}
                        contentStyle={{ direction: 'rtl', fontSize: 13 }}
                      />
                      <Bar dataKey="paid" radius={[8, 8, 0, 0]} barSize={28}>
                        {chartByCampaign.map((entry, i) => {
                          const color = groupColor(entry.group);
                          const dim = hoveredGroup && hoveredGroup !== entry.group;
                          return (
                            <Cell
                              key={i}
                              fill={color}
                              fillOpacity={dim ? 0.22 : 1}
                              style={{ transition: 'fill-opacity 200ms ease' }}
                            />
                          );
                        })}
                        <LabelList
                          dataKey="paid"
                          position="top"
                          formatter={(v: number) => formatEgp(v)}
                          style={{ fontSize: 12, fill: '#253238', fontWeight: 700 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>

          {/* ============ Daily trend ============ */}
          <div className="card">
            <h3>الاتجاه اليومي (آخر 30 يوم)</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, marginBottom: 16 }}>
              إجمالي المحصل يومياً — يوضّح الذروات وحملات معيّنة.
            </p>
            {chartByDay.length === 0 ? (
              <div className="empty">مفيش نشاط لسه.</div>
            ) : (
              <div dir="ltr" style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={chartByDay} margin={{ top: 12, right: 12, bottom: 4, left: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8ea" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => formatEgp(v).replace('ج.م', '')} />
                    <Tooltip formatter={(value: number) => formatEgp(value)} contentStyle={{ direction: 'rtl', fontSize: 13 }} />
                    <Line
                      type="monotone"
                      dataKey="egp"
                      stroke="#009091"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#fec830', stroke: '#009091', strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ============ Detail table ============ */}
          <div className="card">
            <h3>تفاصيل الحملات</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, marginBottom: 12 }}>
              كل حملة بأرقامها الكاملة — للتصدير والمراجعة التفصيلية.
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>الفئة</th>
                  <th>التخصص</th>
                  <th>المحصل</th>
                  <th>النسبة</th>
                  <th>مكتمل</th>
                  <th>قيد المعالجة</th>
                  <th>فشل</th>
                </tr>
              </thead>
              <tbody>
                {byCampaign.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty">
                      مفيش حملات.
                    </td>
                  </tr>
                )}
                {[...byCampaign]
                  .sort((a, b) => Number(b.paid_total_egp) - Number(a.paid_total_egp))
                  .map((r) => {
                    const { group, sub } = splitCampaign(r.title_ar);
                    const share =
                      paidTotal > 0 ? (Number(r.paid_total_egp) / paidTotal) * 100 : 0;
                    const color = groupColor(group);
                    return (
                      <tr key={r.campaign_id}>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              borderRadius: 3,
                              background: color,
                              marginInlineEnd: 8,
                              verticalAlign: 'middle',
                            }}
                          />
                          {group}
                        </td>
                        <td>{sub}</td>
                        <td style={{ fontWeight: 700 }}>{formatEgp(Number(r.paid_total_egp))}</td>
                        <td>
                          <span
                            className="campaign-row-bar"
                            style={{
                              width: `${Math.max(share, 2)}%`,
                              color,
                              minWidth: 8,
                              marginInlineEnd: 8,
                            }}
                          />
                          {share.toFixed(1)}٪
                        </td>
                        <td>{formatNumber(Number(r.paid_count))}</td>
                        <td>{formatNumber(Number(r.pending_count))}</td>
                        <td>{formatNumber(Number(r.failed_count))}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
